const TermsOfService = () => {
    const styles = {
        container: {
            maxWidth: '900px',
            margin: '0 auto',
            padding: '48px 24px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: '#333333',
            textAlign: 'left' as const
        },
        header: {
            textAlign: 'center' as const,
            marginBottom: '48px'
        },
        title: {
            fontSize: '36px',
            fontWeight: 'bold',
            color: '#000000',
            marginBottom: '16px'
        },
        subtitle: {
            fontSize: '18px'
        },
        bold: {
            fontWeight: '600'
        },
        italic: {
            fontStyle: 'italic',
            color: '#666666'
        },
        paragraph: {
            marginBottom: '24px',
            fontSize: '16px',
            color: '#333333'
        },
        h2: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#000000',
            marginTop: '32px',
            marginBottom: '16px'
        },
        h3: {
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#000000',
            marginTop: '24px',
            marginBottom: '12px'
        },
        ul: {
            marginBottom: '24px',
            paddingLeft: '40px',
            listStyleType: 'disc'
        },
        li: {
            marginBottom: '8px',
            fontSize: '16px',
            color: '#333333'
        },
        link: {
            color: '#0066cc',
            textDecoration: 'underline'
        },
        contactSection: {
            marginBottom: '24px'
        },
        divider: {
            textAlign: 'center' as const,
            marginTop: '48px',
            paddingTop: '32px',
            borderTop: '1px solid #cccccc'
        },
        asterisks: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#999999',
            marginBottom: '16px'
        },
        footer: {
            color: '#666666',
            fontStyle: 'italic'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Terms of Service</h1>
                <p style={styles.subtitle}>
                    <span style={styles.bold}>Prodigy Learning</span> | <span style={styles.italic}>Effective Date: November 6, 2025</span>
                </p>
            </div>

            <div>
                <p style={styles.paragraph}>
                    Welcome to Prodigy Learning (
                    <a href="https://www.prodigylearn.com" style={styles.link}>
                        https://www.prodigylearn.com
                    </a>
                    ). These Terms of Service govern your access to and use of our website and services. By accessing or using our website, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.
                </p>

                <h2 style={styles.h2}>1. Acceptance of Terms</h2>
                <p style={styles.paragraph}>
                    By accessing or using the Prodigy Learning website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. These Terms constitute a legally binding agreement between you and Prodigy Learning. We reserve the right to modify these Terms at any time, and such modifications will be effective immediately upon posting. Your continued use of the website after any changes indicates your acceptance of the modified Terms.
                </p>

                <h2 style={styles.h2}>2. Use of Website</h2>

                <h3 style={styles.h3}>2.1 Permitted Use</h3>
                <p style={styles.paragraph}>You may use our website for the following purposes:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Learning about our schools and educational programs</li>
                    <li style={styles.li}>Requesting information about school visits</li>
                    <li style={styles.li}>Inquiring about enrollment opportunities</li>
                    <li style={styles.li}>Contacting us with questions or feedback</li>
                </ul>

                <h3 style={styles.h3}>2.2 Prohibited Use</h3>
                <p style={styles.paragraph}>
                    You agree not to use our website for any unlawful or prohibited purpose, including but not limited to:
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Violating any applicable laws or regulations</li>
                    <li style={styles.li}>Infringing on intellectual property rights of Prodigy Learning or third parties</li>
                    <li style={styles.li}>Transmitting viruses, malware, or other harmful code</li>
                    <li style={styles.li}>Attempting to gain unauthorized access to our systems or networks</li>
                    <li style={styles.li}>Engaging in any activity that disrupts or interferes with our website</li>
                    <li style={styles.li}>Using automated systems or software to extract data from our website without permission</li>
                    <li style={styles.li}>Impersonating any person or entity or misrepresenting your affiliation</li>
                    <li style={styles.li}>Harassing, threatening, or harming others</li>
                </ul>

                <h2 style={styles.h2}>3. Intellectual Property</h2>
                <p style={styles.paragraph}>
                    All content on the Prodigy Learning website, including but not limited to text, graphics, logos, images, videos, audio clips, and software, is the property of Prodigy Learning or its content suppliers and is protected by United States and international copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our website without our prior written consent, except for personal, non-commercial use.
                </p>

                <h2 style={styles.h2}>4. User Submissions</h2>
                <p style={styles.paragraph}>
                    When you submit information, feedback, or other content through our website, including contact forms or inquiries, you grant Prodigy Learning a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, modify, and display such content for the purposes of operating our business, responding to your inquiries, and improving our services. You represent and warrant that you own or have the necessary rights to any content you submit and that such content does not violate any third-party rights or applicable laws.
                </p>

                <h2 style={styles.h2}>5. Third-Party Links</h2>
                <p style={styles.paragraph}>
                    Our website may contain links to third-party websites or services that are not owned or controlled by Prodigy Learning. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that Prodigy Learning shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third-party websites or services.
                </p>

                <h2 style={styles.h2}>6. Disclaimer of Warranties</h2>
                <p style={{...styles.paragraph, fontWeight: 'bold'}}>
                    THE PRODIGY LEARNING WEBSITE AND ALL CONTENT, MATERIALS, AND SERVICES PROVIDED ON OR THROUGH THE WEBSITE ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, PRODIGY LEARNING DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
                    <li style={styles.li}>Warranties that the website will be uninterrupted, error-free, or secure</li>
                    <li style={styles.li}>Warranties regarding the accuracy, completeness, or reliability of any content</li>
                </ul>
                <p style={styles.paragraph}>
                    We do not warrant that the website or its content will meet your requirements or that any defects will be corrected. You use the website at your own risk.
                </p>

                <h2 style={styles.h2}>7. Limitation of Liability</h2>
                <p style={{...styles.paragraph, fontWeight: 'bold'}}>
                    TO THE FULLEST EXTENT PERMITTED BY LAW, PRODIGY LEARNING, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Your access to or use of (or inability to access or use) the website</li>
                    <li style={styles.li}>Any conduct or content of any third party on the website</li>
                    <li style={styles.li}>Any content obtained from the website</li>
                    <li style={styles.li}>Unauthorized access, use, or alteration of your transmissions or content</li>
                </ul>
                <p style={styles.paragraph}>
                    IN NO EVENT SHALL PRODIGY LEARNING'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED ONE HUNDRED DOLLARS ($100) OR THE AMOUNT YOU PAID TO PRODIGY LEARNING IN THE PAST TWELVE MONTHS, WHICHEVER IS GREATER.
                </p>

                <h2 style={styles.h2}>8. Indemnification</h2>
                <p style={styles.paragraph}>
                    You agree to indemnify, defend, and hold harmless Prodigy Learning, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your use of the website, your violation of these Terms, or your violation of any rights of another person or entity.
                </p>

                <h2 style={styles.h2}>9. Privacy</h2>
                <p style={styles.paragraph}>
                    Your use of our website is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
                </p>

                <h2 style={styles.h2}>10. Termination</h2>
                <p style={styles.paragraph}>
                    We reserve the right to terminate or suspend your access to our website at any time, without prior notice or liability, for any reason, including if we believe you have violated these Terms. Upon termination, your right to use the website will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>

                <h2 style={styles.h2}>11. Governing Law and Jurisdiction</h2>
                <p style={styles.paragraph}>
                    These Terms shall be governed by and construed in accordance with the laws of the State in which Prodigy Learning is located, without regard to its conflict of law principles. You agree to submit to the personal and exclusive jurisdiction of the courts located within that State for the resolution of any disputes arising out of or relating to these Terms or your use of the website.
                </p>

                <h2 style={styles.h2}>12. Dispute Resolution</h2>
                <p style={styles.paragraph}>
                    In the event of any dispute, controversy, or claim arising out of or relating to these Terms or your use of the website, the parties agree to first attempt to resolve the dispute through good faith negotiations. If the dispute cannot be resolved through negotiation within thirty (30) days, either party may pursue resolution through the appropriate legal channels.
                </p>

                <h2 style={styles.h2}>13. Entire Agreement</h2>
                <p style={styles.paragraph}>
                    These Terms of Service, together with our Privacy Policy, constitute the entire agreement between you and Prodigy Learning regarding your use of the website and supersede all prior agreements and understandings, whether written or oral, relating to the subject matter herein.
                </p>

                <h2 style={styles.h2}>14. Severability</h2>
                <p style={styles.paragraph}>
                    If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid, illegal, or unenforceable provision shall be deemed modified to the extent necessary to make it valid, legal, and enforceable while preserving its intent.
                </p>

                <h2 style={styles.h2}>15. Waiver</h2>
                <p style={styles.paragraph}>
                    No waiver of any provision of these Terms shall be deemed a further or continuing waiver of such provision or any other provision. Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
                </p>

                <h2 style={styles.h2}>16. Assignment</h2>
                <p style={styles.paragraph}>
                    You may not assign or transfer these Terms or any rights or obligations hereunder without our prior written consent. We may assign or transfer these Terms, in whole or in part, without restriction or notification.
                </p>

                <h2 style={styles.h2}>17. Changes to Terms</h2>
                <p style={styles.paragraph}>
                    We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on this page and updating the effective date. Your continued use of the website after such changes constitutes your acceptance of the modified Terms. We encourage you to review these Terms periodically.
                </p>

                <h2 style={styles.h2}>18. Contact Information</h2>
                <p style={styles.paragraph}>
                    If you have any questions, concerns, or comments about these Terms of Service, please contact us:
                </p>
                <div style={styles.contactSection}>
                    <p style={{...styles.paragraph, fontWeight: '600', marginBottom: '8px'}}>Prodigy Learning</p>
                    <p style={{...styles.paragraph, marginBottom: '8px'}}>
                        Website: <a href="https://www.prodigylearn.com" style={styles.link}>https://www.prodigylearn.com</a>
                    </p>
                    <p style={{...styles.paragraph, marginBottom: '8px'}}>Email: admissions@prodigylearn.com</p>
                </div>

            </div>
        </div>
    );
};

export default TermsOfService;